import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAuth } from "@/lib/route-guards";
import { LoadingProvider } from "@/contexts/loading-context";
import { handleApiError } from "@/lib/error-handler";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type Row,
} from "@tanstack/react-table";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import Marquee from "react-fast-marquee";
import { Button } from "@/components/ui/button";
import { Menu, Edit, Home, List, Play, Trophy, ChevronDown, ChevronUp, Square, X } from "lucide-react";
import { SlidingNumber } from "@/components/ui/shadcn-io/sliding-number";
import { Counter } from "@/components/ui/shadcn-io/counter";
import { motion, AnimatePresence } from "motion/react";
import { FloatingWindow } from "@/components/floating-window";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import "./kura.$kuraId.start-new-design.css";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useKuraStartSignalR } from "@/hooks/use-kura-start-signalr";
import type { ParticipantDto } from "@/hooks/use-kura-start-signalr";
import { toast } from "sonner";
import { useKuraStartDetailQuery } from "@/hooks/use-kura-queries";
import { useSettingsQuery } from "@/hooks/use-settings-queries";
import { Skeleton } from "@/components/ui/skeleton";
import { api, notifyStopDrawOnClose } from "@/lib/api";

// Types for the virtualized table
interface TableColumn {
  key: string;
  label: string;
  columnName: string | null;
  index: number;
}

interface KuraStartDetailForTable {
  primaryTitle?: string;
  backupTitle?: string;
}

// Virtualized Winners Table Component using TanStack Table + TanStack Virtual
// Based on official TanStack example: https://tanstack.com/table/latest/docs/framework/react/examples/virtualized-infinite-scrolling
// Uses a fixed height scrollable container with useVirtualizer
function VirtualizedWinnersTable({
  winners,
  tableColumns,
  kuraStartDetail,
}: {
  winners: ParticipantDto[];
  tableColumns: TableColumn[];
  kuraStartDetail: KuraStartDetailForTable | null | undefined;
}) {
  // Reference to the scrollable container
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Create stable column definitions using useMemo
  const columns = useMemo<ColumnDef<ParticipantDto>[]>(() => {
    return tableColumns.map((col) => ({
      id: col.key,
      accessorFn: (row: ParticipantDto) => col.columnName ? row[col.columnName] : null,
      header: col.label,
      cell: ({ getValue, row }) => {
        const value = getValue();
        const isDurumColumn = col.label.toLowerCase().includes("durum");
        const primaryTitle = kuraStartDetail?.primaryTitle?.toString().toLowerCase();
        const backupTitle = kuraStartDetail?.backupTitle?.toString().toLowerCase();
        const currentValue = value?.toString().toLowerCase();
        const rowIndex = row.index;
        
        if (isDurumColumn && value) {
          return (
            <span
              className={`px-3 py-1 rounded-full text-base font-semibold ${
                currentValue === primaryTitle
                  ? "bg-[#10B98133] text-[#10B981] border border-[#10B9814D]"
                  : currentValue === backupTitle
                    ? "bg-[#FDE68A33] text-[#D97706] border border-[#D977064D]"
                    : "bg-gray-100 text-gray-800"
              }`}
            >
              {String(value)}
            </span>
          );
        }
        
        return (
          <span style={{ color: rowIndex === 0 ? "#fff" : "#101010" }}>
            {value != null ? String(value) : "-"}
          </span>
        );
      },
    }));
  }, [tableColumns, kuraStartDetail?.primaryTitle, kuraStartDetail?.backupTitle]);

  // Create TanStack Table instance
  const table = useReactTable({
    data: winners,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();

  // Use container-based virtualizer (as per official TanStack example)
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 72, // Estimate row height for accurate scrollbar
    getScrollElement: () => tableContainerRef.current,
    // Measure dynamic row height, except in Firefox
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 10,
  });

  // Empty state
  if (winners.length === 0) {
    return (
      <div className="flex justify-center py-20 pt-6 px-6 md:p-12 md:pb-20 md:pt-6 h-[100vh]">
        <div className="max-w-full whitespace-normal">
          <p className="text-sm text-center text-foreground font-semibold">
            Kazananlar henüz belirlenmedi.
          </p>
          <p className="text-sm text-primary/50 text-center font-semibold">
            Kura çekilişi sırasında kazananlar burada görüntülenecektir.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={tableContainerRef}
      style={{
        overflow: 'auto', // Scrollable container
        position: 'relative', // Needed for sticky header
        height: '100vh', // Fixed height - adjust as needed
        minHeight: '100vh',
      }}
    >
      {/* Using CSS Grid for table layout as per TanStack example */}
      <table style={{ display: 'grid', width: '100%' }}>
        {/* Sticky Header */}
        <thead
          style={{
            display: 'grid',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${tableColumns.length}, minmax(0, 1fr))`,
                width: '100%',
                background: '#fff',
              }}
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="py-6 px-4 text-base font-extrabold leading-none text-[#363636] whitespace-normal text-center"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        
        {/* Virtualized Body */}
        <tbody
          style={{
            display: 'grid',
            height: `${rowVirtualizer.getTotalSize()}px`, // Tells scrollbar how big the table is
            position: 'relative', // Needed for absolute positioning of rows
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index] as Row<ParticipantDto>;
            const rowIndex = virtualRow.index;
            const rowBackground =
              rowIndex === 0
                ? "linear-gradient(111.39deg, #5367B0 15.9%, #394D96 89.86%)"
                : rowIndex % 2 === 1
                  ? "#FFFFFF"
                  : "rgba(82, 102, 175, 0.2)";

            return (
              <tr
                data-index={virtualRow.index} // Needed for dynamic row height measurement
                ref={(node) => rowVirtualizer.measureElement(node)} // Measure dynamic row height
                key={row.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${tableColumns.length}, minmax(0, 1fr))`,
                  position: 'absolute',
                  transform: `translateY(${virtualRow.start}px)`, // This should always be a style as it changes on scroll
                  width: '100%',
                  background: rowBackground,
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="text-base font-semibold leading-normal py-4 px-4 whitespace-normal flex items-center justify-center text-center"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export const Route = createFileRoute("/kura/$kuraId/start-new-design-3")({
  beforeLoad: requireAuth,
  component: () => (
    <LoadingProvider>
      <KuraStartPage />
    </LoadingProvider>
  ),
});

function KuraStartPage() {
  const { kuraId } = Route.useParams();

  // Fetch kura start details from API
  const {
    data: kuraStartDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useKuraStartDetailQuery(kuraId);

  // Counter states - these will update during the kura draw
  // Counter shows the current winner's Çekiliş Sıra No (starts at 0)
  const [counter1, setCounter1] = useState(0); // Current winner's draw sequence number
  const [counter2, setCounter2] = useState(0); // Property counter (if needed)

  // Animation trigger keys - increment these to force all digits to animate
  const [animationKey1, setAnimationKey1] = useState(0);
  const [animationKey2, setAnimationKey2] = useState(0);

  // Remaining participants count from SignalR (KalanHakSahibi from backend)
  const [kalanHakSahibi, setKalanHakSahibi] = useState<number | null>(null);

  // SignalR connection state
  const [kuraStarted, setKuraStarted] = useState(false);

  // Ref to track if stop was requested (to prevent race condition with async start API)
  const stopRequestedRef = useRef(false);

  // Ref to track if start toast has been shown (to avoid showing it multiple times)
  const startToastShownRef = useRef(false);

  // State to track if we're in single draw mode (Ctrl+Y)
  const [isSingleDrawMode, setIsSingleDrawMode] = useState(false);

  // State to track if we're waiting for KuraStopped after single draw
  const [waitingForKuraStopped, setWaitingForKuraStopped] = useState(false);

  // Winners list from SignalR
  const [winners, setWinners] = useState<ParticipantDto[]>([]);

  // Quota categories state from SignalR updates
  const [quotaCategories, setQuotaCategories] = useState<
    Array<{
      label: string;
      kontenjan: number;
      toplamKatilimci: number;
    }>
  >([]);

  // State to track first row (stats section) visibility
  const [isFirstRowVisible, setIsFirstRowVisible] = useState(true);

  // Context menu states
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Floating quota table states
  // Floating windows state - simplified
  const [showFloatingQuotaTable, setShowFloatingQuotaTable] = useState(false);

  // Fetch settings using React Query
  const { data: settings } = useSettingsQuery();

  // Get lottery kind from API response
  // 1 = Sadece Katılımcı, 2 = Katılımcı + Konut, 3 = Sadece Konut
  const lotteryKind = kuraStartDetail?.lotteryKind || 1;
  // Kind 4 ("Katılımcı + Konut + Diğer + 65 Yaş") canlı çekilişte kind 2 ile aynı davranır;
  // farkı yalnızca konutun hangi katılımcıya gittiğidir.
  const isParticipantPropertyKind = lotteryKind === 2 || lotteryKind === 4;

  // Initialize KalanHakSahibi from API response when data loads
  useEffect(() => {
    if (kuraStartDetail && kalanHakSahibi === null) {
      // If backend provides KalanHakSahibi in initial response, use it
      // Otherwise, use total participant count as initial value
      const initialValue =
        kuraStartDetail.kalanHakSahibi ?? kuraStartDetail.participantTotalCount;
      setKalanHakSahibi(initialValue);
    }
    const list = kuraStartDetail?.resultList ?? [];

    if (list.length > 0) {
      setWinners((prev) => {
        // If API has more results than current state (e.g., SignalR delivered
        // a few winners before the API query resolved), use API data since
        // it contains the full historical result set.
        if (prev.length >= list.length) return prev;
        return list;
      });
    }

    // Initialize quota categories from API response
    if (
      kuraStartDetail?.lotteryApplicationTypeDtos &&
      quotaCategories.length === 0
    ) {
      setQuotaCategories(kuraStartDetail.lotteryApplicationTypeDtos);
    }
  }, [kuraStartDetail, kalanHakSahibi, quotaCategories.length]);

  // Keep a ref to kuraStartDetail to avoid stale closure in SignalR callbacks
  const kuraStartDetailRef = useRef(kuraStartDetail);
  useEffect(() => {
    kuraStartDetailRef.current = kuraStartDetail;
  }, [kuraStartDetail]);

  // SignalR callback for new winner - using useCallback to maintain stable reference
  const handleNewWinner = useCallback((participant: Record<string, any>) => {
    // Only set kuraStarted to true if stop was not requested
    // This prevents race condition where SignalR events arrive after user clicked stop
    if (!stopRequestedRef.current) {
      setKuraStarted(true);
    }

    // Add the new winner at the beginning of the list (latest first)
    setWinners((prev) => [participant, ...prev]);

    // Log the winner data to console
    // console.log("New winner data:", participant);

    // Update KalanHakSahibi value from backend SignalR response
    // Backend sends this value with each winner update
    if (
      participant.KalanHakSahibi !== undefined &&
      participant.KalanHakSahibi !== null
    ) {
      const kalanValue =
        typeof participant.KalanHakSahibi === "string"
          ? parseInt(participant.KalanHakSahibi, 10)
          : participant.KalanHakSahibi;
      // console.log("Updating KalanHakSahibi from backend:", kalanValue);
      setKalanHakSahibi(kalanValue);
    }

    // Update quota categories if provided in the winner data
    if (
      participant.LotteryApplicationTypes &&
      Array.isArray(participant.LotteryApplicationTypes)
    ) {
      setQuotaCategories(participant.LotteryApplicationTypes);
    }

    // Use ref to get latest kuraStartDetail value
    const currentKuraStartDetail = kuraStartDetailRef.current;

    // Update counter1 to show current winner's Participant Sıra No
    // Find the participant column with isOrderNo flag (totalParticipants is not null)
    const participantOrderNoColumn =
      currentKuraStartDetail?.getLotterySettingsList.find(
        (col) => col.isOrderNo && col.totalParticipants !== null
      );
    if (participantOrderNoColumn) {
      const columnName = participantOrderNoColumn.columnName;
      if (columnName && participant[columnName] !== undefined) {
        const siraNo = participant[columnName];
        const siraNoNumber =
          typeof siraNo === "string" ? parseInt(siraNo, 10) : siraNo;
        setCounter1(siraNoNumber);
        setAnimationKey1((prev) => prev + 1); // Trigger animation for all digits
      }
    }

    // Update counter2 to show current winner's Property Sıra No
    // Find the property column with isOrderNo flag (totalProperties is not null)
    const propertyOrderNoColumn =
      currentKuraStartDetail?.getLotterySettingsList.find(
        (col) => col.isOrderNo && col.totalProperties !== null
      );
    if (propertyOrderNoColumn) {
      const columnName = propertyOrderNoColumn.columnName;
      if (columnName && participant[columnName] !== undefined) {
        const propertySiraNo = participant[columnName];
        const propertySiraNoNumber =
          typeof propertySiraNo === "string"
            ? parseInt(propertySiraNo, 10)
            : propertySiraNo;
        setCounter2(propertySiraNoNumber);
        setAnimationKey2((prev) => prev + 1); // Trigger animation for all digits
      } else {
        setCounter2(0);
        setAnimationKey2((prev) => prev + 1); // Trigger animation for all digits
      }
    }
  }, []); // Empty deps - uses refs for latest values

  // SignalR callback for kura completed
  const handleKuraCompleted = useCallback(() => {
    setKuraStarted(false);
    // Reset stop flag when kura completes
    stopRequestedRef.current = false;
    // Reset single draw mode states
    setIsSingleDrawMode(false);
    setWaitingForKuraStopped(false);
    // Show completion message
    toast.success(
      <span className="text-xl font-extrabold whitespace-nowrap">
        KURA ÇEKİLİŞİ TAMAMLANDI
      </span>,
      {
        description: (
          <span className="text-xl font-semibold whitespace-nowrap">
             KURA TAMAMLANMIŞTIR.
          </span>
        ),
        duration: 5000,
      }
    );
  }, []);

  // SignalR callback for counter update
  const handleCounterUpdate = useCallback((counter1Val?: number, counter2Val?: number) => {
    if (counter1Val !== undefined) {
      setCounter1(counter1Val);
      setAnimationKey1((prev) => prev + 1);
    }
    if (counter2Val !== undefined) {
      setCounter2(counter2Val);
      setAnimationKey2((prev) => prev + 1);
    }
  }, []);

  // SignalR callback for kura stopped
  const handleKuraStopped = useCallback(() => {
    // When KuraStopped is received after single draw
    setWaitingForKuraStopped(false);
    setIsSingleDrawMode(false);
    setKuraStarted(false);
    toast.info(
      <span className="text-xl font-semibold whitespace-nowrap">
        TEK ÇEKİLİŞ TAMAMLANDI
      </span>,
      {
        duration: 3000,
      }
    );
  }, []);

  // Initialize SignalR connection for kura start
  const signalR = useKuraStartSignalR({
    kuraId,
    onNewWinner: handleNewWinner,
    onKuraCompleted: handleKuraCompleted,
    onCounterUpdate: handleCounterUpdate,
    onKuraStopped: handleKuraStopped,
  });
  // InOrder to notify backend when user leaves the page while kura is being drawn
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!kuraStarted) return;

      notifyStopDrawOnClose(kuraId);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [kuraId, kuraStarted]);

  // Keyboard shortcuts: Ctrl+B to start (continuous), Ctrl+Y for single draw, Ctrl+M to stop kura
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+B: Start kura (continuous mode, SingleDraw=false)
      if (event.ctrlKey && event.key === "b") {
        event.preventDefault();
        // Block if waiting for KuraStopped after single draw
        if (waitingForKuraStopped) {
          toast.warning("Lütfen tek çekiliş tamamlanmasını bekleyin.");
          return;
        }
        if (!kuraStarted) {
          handleStartKura(false); // SingleDraw = false
        }
        return;
      }

      // Ctrl+Y: Single draw (for Sadece Katılımcı and Katılımcı + Konut)
      if (event.ctrlKey && event.key === "y") {
        event.preventDefault();
        // Check if single draw is enabled for this lottery
        if (!kuraStartDetail?.isTekCekilis) {
          return; // Silently ignore if single draw is disabled
        }
        // Only available for lotteryKind 1 (Sadece Katılımcı) and 2 (Katılımcı + Konut)
        if (lotteryKind !== 1 && lotteryKind !== 2) {
          toast.warning(
            "Tek çekiliş sadece 'Sadece Katılımcı' ve 'Katılımcı + Konut' türündeki kuralar için kullanılabilir."
          );
          return;
        }
        // Block if waiting for KuraStopped
        if (waitingForKuraStopped) {
          toast.warning("Lütfen tek çekiliş tamamlanmasını bekleyin.");
          return;
        }
        // Block if kura is already started in continuous mode
        if (kuraStarted && !isSingleDrawMode) {
          toast.warning("Kura devam ederken tek çekiliş yapılamaz.");
          return;
        }
        handleSingleDraw();
        return;
      }

      // Ctrl+M: Stop kura
      if (event.ctrlKey && event.key === "m") {
        event.preventDefault();
        // Block if waiting for KuraStopped after single draw
        if (waitingForKuraStopped) {
          toast.warning(
            "Tek çekiliş modu durdurulamaz. Lütfen çekilişin tamamlanmasını bekleyin."
          );
          return;
        }
        // Block if in single draw mode
        if (isSingleDrawMode) {
          toast.warning(
            "Tek çekiliş modu durdurulamaz. Lütfen çekilişin tamamlanmasını bekleyin."
          );
          return;
        }
        if (kuraStarted) {
          handleStopKura();
        }
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kuraStarted, lotteryKind, waitingForKuraStopped, isSingleDrawMode]);

  // Function to start the kura draw - calls backend API
  const handleStartKura = async (singleDraw: boolean = false) => {
    try {
      // Reset flags when starting
      stopRequestedRef.current = false;
      startToastShownRef.current = false;

      // Set kuraStarted immediately to update UI (button changes to stop)
      setKuraStarted(true);

      startToastShownRef.current = true;

      // Determine the endpoint based on lotteryKind
      // 1 = Sadece Katılımcı, 2 = Katılımcı + Konut, 3 = Sadece Konut
      // Kind 4 kendi kuyruğunu ve tüketicisini kullanır.
      const endpoint =
        lotteryKind === 4
          ? `/lotteries/${kuraId}/start-realtype`
          : isParticipantPropertyKind
            ? `/lotteries/${kuraId}/start-participant-property`
            : `/lotteries/${kuraId}/start`;

      // Call backend REST API to start the draw with SingleDraw parameter
      const response = await api.post(endpoint, null, {
        params: { SingleDraw: singleDraw },
      });

      if (response.status !== 200) {
        throw new Error("Kura başlatılamadı");
      }
    } catch (error: any) {
      console.error("Error starting kura:", error);

      // Extract user-friendly error message
      const errorMessage = handleApiError(error, "Kura başlatılamadı");
      toast.error(errorMessage);
      setKuraStarted(false);
    }
  };

  // Function to start single draw (Ctrl+Y) - for Sadece Katılımcı and Katılımcı + Konut
  const handleSingleDraw = async () => {
    try {
      // Reset flags when starting
      stopRequestedRef.current = false;
      startToastShownRef.current = false;

      // Set flags for single draw mode
      setIsSingleDrawMode(true);
      setWaitingForKuraStopped(true);
      setKuraStarted(true);

      // Determine the endpoint based on lotteryKind
      // 1 = Sadece Katılımcı -> /lotteries/{id}/start
      // 2 = Katılımcı + Konut -> /lotteries/{id}/start-participant-property
      // Kind 4 kendi kuyruğunu ve tüketicisini kullanır.
      const endpoint =
        lotteryKind === 4
          ? `/lotteries/${kuraId}/start-realtype`
          : isParticipantPropertyKind
            ? `/lotteries/${kuraId}/start-participant-property`
            : `/lotteries/${kuraId}/start`;

      // Call backend REST API with SingleDraw=true
      const response = await api.post(endpoint, null, {
        params: { SingleDraw: true },
      });

      if (response.status !== 200) {
        throw new Error("Tek çekiliş başlatılamadı");
      }

      toast.info(
        <span className="text-lg font-semibold whitespace-nowrap">
          TEK ÇEKİLİŞ BAŞLATILDI
        </span>,
        {
          duration: 2000,
        }
      );
    } catch (error: any) {
      console.error("Error starting single draw:", error);

      // Extract user-friendly error message
      const errorMessage = handleApiError(error, "Tek çekiliş başlatılamadı");
      toast.error(errorMessage);

      // Reset states on error
      setIsSingleDrawMode(false);
      setWaitingForKuraStopped(false);
      setKuraStarted(false);
    }
  };

  // Function to stop the kura draw
  const handleStopKura = async () => {
    try {
      // Prevent stopping if in single draw mode
      if (isSingleDrawMode || waitingForKuraStopped) {
        toast.warning(
          "Tek çekiliş modu durdurulamaz. Lütfen çekilişin tamamlanmasını bekleyin."
        );
        return;
      }

      // Set flag to prevent start toast from showing if start API is still in flight
      stopRequestedRef.current = true;

      if (!signalR.isConnected) {
        toast.error("Bağlantı kurulamadı.");
        return;
      }

      // Call backend REST API to stop the draw
      const response = await api.post(`/lotteries/${kuraId}/stop-draw`);

      if (response.status !== 200) {
        throw new Error("Kura durdurulamadı");
      }

      setKuraStarted(false);
      toast.success(
        <span className="text-2xl font-extrabold whitespace-nowrap">
          KURA ÇEKİLİŞİ DURDURULDU
        </span>,
        {
          duration: 5000,
          className: "p-12 flex items-center",
        }
      );
    } catch (error) {
      console.error("Error stopping kura:", error);
      const errorMessage = handleApiError(error, "Kura durdurulamadı");
      toast.error(errorMessage);
    }
  };

  // Context menu handlers
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleToggleFloatingQuotaTable = () => {
    setShowFloatingQuotaTable((prev) => !prev);
    setContextMenu(null);
  };

  const handleCloseFloatingQuotaTable = () => {
    setShowFloatingQuotaTable(false);
  };

  // Close context menu when clicking anywhere
  useEffect(() => {
    const handleClick = () => handleCloseContextMenu();
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Get kura name from API data
  const kuraName = kuraStartDetail?.lotteryName || "";

  // Marquee text from settings API
  const marqueeText = settings?.description || "";

  // Calculate digit counts for counters based on actual totals
  const participantDigits = Math.max(
    2,
    (kuraStartDetail?.participantTotalCount || 0).toString().length
  );
  const propertyDigits = Math.max(
    2,
    (kuraStartDetail?.propertiesTotalCount || 0).toString().length
  );

  // Get dynamic counter styling based on digit count
  // Using clamp() for responsive sizing: clamp(min, preferred, max)
  const getCounterStyle = (value: string) => {
    const digitCount = value.length;
    if (digitCount <= 3) {
      return {
        fontSize: "clamp(2rem, 3vw, 2.5rem)", // 32px min, 3vw preferred, 40px max
        padding: "clamp(1rem, 2vw, 1.625rem) clamp(0.875rem, 1.8vw, 1.5rem)", // vertical: 16-26px, horizontal: 14-24px
        gap: "clamp(0.5rem, 1.2vw, 1.25rem)", // 8px min, 1.2vw preferred, 20px max
      };
    }
    if (digitCount === 4) {
      return {
        fontSize: "clamp(1.875rem, 2.8vw, 2.375rem)", // 30px min, 2.8vw, 38px max
        padding:
          "clamp(0.875rem, 1.8vw, 1.5rem) clamp(0.75rem, 1.6vw, 1.375rem)", // vertical: 14-24px, horizontal: 12-22px
        gap: "clamp(0.5rem, 1vw, 1rem)", // 8px min, 1vw, 16px max
      };
    }
    if (digitCount === 5) {
      return {
        fontSize: "clamp(1.75rem, 2.5vw, 2.125rem)", // 28px min, 2.5vw, 34px max
        padding:
          "clamp(0.75rem, 1.5vw, 1.25rem) clamp(0.625rem, 1.4vw, 1.125rem)", // vertical: 12-20px, horizontal: 10-18px
        gap: "clamp(0.5rem, 0.9vw, 0.875rem)", // 8px min, 0.9vw, 14px max
      };
    }
    if (digitCount === 6) {
      return {
        fontSize: "clamp(1.5rem, 2.2vw, 1.875rem)", // 24px min, 2.2vw, 30px max
        padding: "clamp(0.625rem, 1.2vw, 1rem) clamp(0.5rem, 1vw, 0.875rem)", // vertical: 10-16px, horizontal: 8-14px
        gap: "clamp(0.375rem, 0.75vw, 0.75rem)", // 6px min, 0.75vw, 12px max
      };
    }
    // For 7+ digits
    return {
      fontSize: "clamp(1.25rem, 1.9vw, 1.625rem)", // 20px min, 1.9vw, 26px max
      padding: "clamp(0.5rem, 1vw, 0.75rem) clamp(0.375rem, 0.8vw, 0.625rem)", // vertical: 8-12px, horizontal: 6-10px
      gap: "clamp(0.25rem, 0.6vw, 0.5rem)", // 4px min, 0.6vw, 8px max
    };
  };

  // Image URL from API - use lotteryImage if available, otherwise use settings byteDosya
  // Add base64 prefix for byteDosya images
  const getImageUrl = () => {
    if (kuraStartDetail?.lotteryImage) {
      // Eğer zaten data:image ile başlamıyorsa prefix ekle
      if (!kuraStartDetail.lotteryImage.startsWith("data:image")) {
        return `data:image/png;base64,${kuraStartDetail.lotteryImage}`;
      }
      return kuraStartDetail.lotteryImage;
    }

    if (settings?.byteDosya) {
      return `data:image/png;base64,${settings.byteDosya}`;
    }

    return "";
  };

  const kuraImageUrl = getImageUrl();

  // Get all column labels from API (including both participant and property columns)
  // Backend only returns visible columns in order
  const allColumns = kuraStartDetail?.getLotterySettingsList || [];

  // Table columns configuration from API
  // Map columns with their columnName to match with winner data
  const tableColumns = allColumns.map((setting, index) => ({
    key: `${setting.label}_${index}`, // Use label + index to ensure unique keys
    label: setting.label,
    columnName: setting.columnName, // Store columnName to match winner data
    index: index, // Store index for fallback
  }));

  // Slider texts - fixed texts for the slider
  const sliderTexts = [
    "Hak sahibi sıra numaraları TOPLU KONUT İDARESİ BAŞKANLIĞI resmi internet sayfasında bulunan duyurular bölümündeki ilgili projenin bilgilendirme bölümünde yayınlanmıştır.",
    "Kura çekilişlerimiz K.V.K.K. gereği isim ve kimlik numaraları gizli tutularak gerçekleştirilmektedir.",
    "Kura Çekilişlerimiz Ankara 66. Noterliği denetiminde yapılmaktadır.",
    "Kura çekiliş sonuçları noter onayından sonra TOPLU KONUT İDARESİ BAŞKANLIĞI resmi internet sayfası www.toki.gov.tr adresinden ilan edilecektir.",
  ];

  // Initialize Embla Carousel with autoplay and disabled interactions
  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      watchDrag: false, // Disable mouse drag
      watchSlides: false, // Disable slide watching
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  // Show loading state while fetching data
  if (isDetailLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto p-8">
          <div className="space-y-6">
            <div className="container mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Image skeleton */}
              <div className="lg:col-span-8 xl:col-span-6 lg:order-2">
                <Skeleton className="w-full h-[400px] rounded-lg" />
              </div>
              {/* Stats skeleton */}
              <div className="lg:col-span-4 xl:col-span-3 lg:order-1 bg-card border rounded-lg shadow-sm">
                <div className="p-6 space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if data fetch failed
  if (detailError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-red-600">
            Veri yüklenirken hata oluştu
          </h2>
          <p className="text-muted-foreground">
            {detailError?.message || "Bilinmeyen hata"}
          </p>
          <Button onClick={() => window.location.reload()}>Tekrar Dene</Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-background isolate relative font-[Inter]"
      onContextMenu={handleContextMenu}
    >
      {/* Fullscreen kura start page without header/sidebar */}
      <div className="mx-auto min-h-screen flex flex-col">
        <div className="space-y-6 lg:space-y-4 mb-8">
          {/* First Row: 3 columns - Stats list in first column (smaller), second column larger */}
          <div className="container-fluid mx-auto px-4 xl:px-6 pt-6">
            {/* Collapsible First Row Content */}
            <AnimatePresence 
              initial={false}
              onExitComplete={() => {
                // Scroll to top after hide animation completes
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {isFirstRowVisible && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ 
                    duration: 0.3, 
                    ease: "easeInOut" 
                  }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-12 items-start pb-4 xl:pb-1">
                    {/* First Column: Stats List (shows second on mobile/tablet) */}
                    <div className="lg:sticky lg:top-0 lg:col-span-3 order-2 lg:order-1 flex flex-col">
                      {/* Stats Section */}
                      <div
                className="px-4 py-4 space-y-4 bg-[#171D49] rounded-xl"
                style={{
                  border: "1px solid #D4D4D4",
                  boxShadow: "0px 4px 11px 5px #7272721A",
                }}
              >
                {/* Row 1: Toplam Hak Sahibi (and Toplam Konut Sayısı for lotteryKind 2) */}
                <div className={isParticipantPropertyKind ? "grid grid-cols-2 gap-2 mb-4" : "mb-4"}>
                  <div
                    className="p-4 rounded-lg border-muted relative isolate bg-[#fff]"
                    style={{
                      border: "2px solid #374151",
                      boxShadow: "0px 7px 20px 9px #7272720A",
                    }}
                  >
                    <span className="text-base font-semibold leading-none text-[#181818]">
                      Toplam Hak Sahibi
                    </span>
                    <span
                      className="text-[40px] font-semibold leading-none block mt-2 text-[#252525]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {isDetailLoading
                        ? "..."
                        : kuraStartDetail?.participantTotalCount?.toString() ||
                          "0"}
                    </span>
                  </div>

                  {/* Toplam Konut Sayısı - Only shown for lotteryKind 2 (Katılımcı + Konut) */}
                  {isParticipantPropertyKind && (
                    <div
                      className="p-4 rounded-lg border-muted relative isolate bg-[#fff]"
                      style={{
                        border: "2px solid #374151",
                        boxShadow: "0px 7px 20px 9px #7272720A",
                      }}
                    >
                      <span className="text-base font-semibold leading-none text-[#181818]">
                        Toplam Konut Sayısı
                      </span>
                      <span
                        className="text-[40px] font-semibold leading-none block mt-2 text-[#252525]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {isDetailLoading
                          ? "..."
                          : kuraStartDetail?.propertiesTotalCount?.toString() ||
                            "0"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Row 2: Asil Sayısı and Kalan Hak Sahibi side by side */}
                <div className="grid grid-cols-2 gap-2 mb-[30px]">
                  {/* Asil Sayısı Box */}
                  <div
                    className="text-center p-4 rounded-lg border-muted relative isolate bg-[#fff]"
                    style={{
                      border: "2px solid #374151",
                      boxShadow: "0px 7px 20px 9px #7272720A",
                    }}
                  >
                    <span
                      className="text-[32px] font-semibold leading-none block mb-4 text-[#252525] text-center"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {isDetailLoading
                        ? "..."
                        : kuraStartDetail?.primaryCount?.toString() || "0"}
                    </span>
                    <span className="text-base font-semibold leading-none text-[#181818]">
                      Asil Sayısı
                    </span>
                  </div>

                  {/* Kalan Hak Sahibi Box - Shows remaining participants from backend */}
                  <div
                    className="text-center p-4 rounded-lg border-muted relative isolate bg-[#fff]"
                    style={{
                      border: "2px solid #374151",
                      boxShadow: "0px 7px 20px 9px #7272720A",
                    }}
                  >
                    <span
                      className="text-[32px] font-semibold leading-none block mb-4 text-[#252525] text-center"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {isDetailLoading
                        ? "..."
                        : (kalanHakSahibi !== null
                            ? kalanHakSahibi
                            : kuraStartDetail?.participantTotalCount || 0
                          ).toString()}
                    </span>
                    <span className="text-base font-semibold leading-none text-[#181818]">
                      Kalan Hak Sahibi
                    </span>
                  </div>
                </div>

                {/* Row 3: Slider Section */}
                <div className="flex-1 flex flex-col">
                  {/* Embla Viewport - Non-interactive */}
                  <div
                    className="rounded-lg px-4 py-5"
                    style={{
                      boxShadow: "0px 4px 6px 4.5px #5367B01F",
                      border: "1px solid #5367B0",
                    }}
                  >
                    <div
                      className="overflow-hidden h-full pointer-events-none"
                      ref={emblaRef}
                    >
                      {/* Embla Container */}
                      <div className="flex h-full">
                        {sliderTexts.map((text, index) => (
                          <div
                            key={index}
                            className="flex-[0_0_100%] min-w-0 flex items-center justify-center"
                          >
                            <p className="text-sm font-semibold leading-5 text-white">
                              {text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-9 lg:order-2 h-full">
              <div className="column-wrapper isolate h-full relative grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Second Column - Kura Image (shows first on mobile/tablet) */}
                <div className="lg:col-span-8 lg:order-1 h-full flex flex-col items-center py-2 px-4">
                  {/* Image Row */}
                  <div className="flex flex-col items-center justify-center w-full mb-6 sticky top-4">
                    {isDetailLoading || !kuraImageUrl ? (
                      <Skeleton className="w-full h-[130px] rounded-lg" />
                    ) : (
                      <img
                        src={kuraImageUrl}
                        alt="Kura Görseli"
                        className="w-full h-full object-contain lg:max-w-[75%] rounded-lg"
                      />
                    )}
                    <div className="w-full">
                      {/* Kura Name Row */}
                      <div className="container mx-auto text-center mt-4">
                        <h2 className="text-2xl lg:text-5xl font-bold leading-none text-[#FFFFFF]">
                          {kuraName}
                        </h2>
                      </div>
                      {/* Counters */}
                      <div className="flex flex-col items-center gap-4 mt-8">
                        <div>
                          {/* Counter 1 - Participant Counter (show for lotteryKind 1 and 2) */}
                        {(lotteryKind === 1 || isParticipantPropertyKind) && (
                          <div className="flex flex-col items-center">
                            {isParticipantPropertyKind && (
                              <span className="w-full text-start text-lg lg:text-xl font-semibold text-[#FFFFFF] mb-2">
                                Hak Sahibi
                              </span>
                            )}
                            <div className="flex justify-center">
                              <div
                                className="flex justify-center items-center"
                                style={{
                                  gap: getCounterStyle("000").gap,
                                }}
                              >
                                {counter1
                                  .toString()
                                  .padStart(participantDigits, "0")
                                  .split("")
                                  .map((digit, index) => (
                                    <div
                                      key={`counter1-${index}`}
                                      className="slot-counter-char flex items-center justify-center"
                                    >
                                      <SlidingNumber
                                        number={parseInt(digit)}
                                        animateAll={true}
                                        forceAnimationKey={animationKey1}
                                        className="slot-counter-number"
                                        style={{
                                          fontSize: getCounterStyle("000").fontSize,
                                          padding: getCounterStyle("000").padding,
                                        }}
                                      />
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Counter 2 - Property Counter (show for lotteryKind 2 and 3) */}
                        {(isParticipantPropertyKind || lotteryKind === 3) && (
                          <div className="flex flex-col items-center mt-4">
                            {isParticipantPropertyKind && (
                              <span className="w-full text-start text-lg lg:text-xl font-semibold text-[#FFFFFF] mb-2">
                                Konut
                              </span>
                            )}
                            <div className="flex w-full">
                              <div
                                className="flex justify-center items-center"
                                style={{
                                  gap: getCounterStyle("000").gap,
                                }}
                              >
                                {counter2
                                  .toString()
                                  .padStart(propertyDigits, "0")
                                  .split("")
                                  .map((digit, index) => (
                                    <div
                                      key={`counter2-${index}`}
                                      className="slot-counter-char flex items-center justify-center"
                                    >
                                      <SlidingNumber
                                        number={parseInt(digit)}
                                        animateAll={true}
                                        forceAnimationKey={animationKey2}
                                        className="slot-counter-number"
                                        style={{
                                          fontSize: getCounterStyle("000").fontSize,
                                          padding: getCounterStyle("000").padding,
                                        }}
                                      />
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Toggle Button for First Row */}
            <div className="group flex justify-center pb-1">
              <button
                onClick={() => setIsFirstRowVisible(!isFirstRowVisible)}
                className="p-2 lg:-mt-4 hover:bg-white/10 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                aria-label={isFirstRowVisible ? "Hide stats section" : "Show stats section"}
              >
                {isFirstRowVisible ? (
                  <ChevronUp className="h-5 w-5 text-[#C6C6C6]" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-[#C6C6C6]" />
                )}
              </button>
            </div>
          </div>

          {/* Third Row: Winners Table (Virtualized) */}
          <div className="space-y-6 mt-8 lg:mt-0">
            {/* Winners Table */}
            <div
              className="bg-card overflow-hidden"
              style={{
                border: "1px solid #D9D9D9",
                boxShadow: "0px 4px 11px 5px #7272721A",
                borderRadius: "12px",
              }}
            >
              {/* Virtualized Table with Sticky Header (built-in) */}
              <VirtualizedWinnersTable
                winners={winners}
                tableColumns={tableColumns}
                kuraStartDetail={kuraStartDetail}
              />
            </div>
          </div>
        </div>

        {/* Sticky Footer with Marquee and FAB */}
        <div className="sticky bottom-4 z-50 mt-auto mx-4">
          <div className="flex items-center gap-4">
            {/* Marquee Section - flex-grow to take available space but with min-width 0 to allow shrinking */}
            <div
              className="flex-grow min-w-0 rounded-lg py-3 bg-[#5367B0]"
              style={{
                boxShadow: "0px 4px 11px 3px #7272721A",
              }}
            >
              <Marquee speed={50} gradient={false}>
                <span className="text-sm font-semibold leading-5 text-white mx-8">
                  {marqueeText}
                </span>
              </Marquee>
            </div>

            {/* Button Section - fixed width to prevent being pushed out */}
            <div className="flex-shrink-0 w-12">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    size="icon"
                    className="rounded-full w-12 h-12 shadow-lg bg-[#5367B0] hover:bg-[#394D96]"
                    aria-label="Menu"
                  >
                    <Menu className="h-7 w-7" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="end"
                  sideOffset={10}
                  className="w-auto p-2"
                >
                  <div className="flex flex-col gap-2">
                    {/* Homepage Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link to="/" className="flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        Ana Sayfa
                      </Link>
                    </Button>

                    {/* Kura List Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link
                        to="/kura"
                        search={{ search: "" }}
                        className="flex items-center gap-2"
                      >
                        <List className="h-4 w-4" />
                        Kura Listesi
                      </Link>
                    </Button>

                    {/* Kura Detail Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link
                        to="/kura/$kuraId/edit"
                        params={{ kuraId }}
                        className="flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Kura Detay
                      </Link>
                    </Button>

                    {/* Winners List Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link
                        to="/kura/$kuraId/winners"
                        params={{ kuraId }}
                        className="flex items-center gap-2"
                      >
                        <Trophy className="h-4 w-4" />
                        Kazanan Listesi
                      </Link>
                    </Button>

                    {/* Start Kura Button */}
                    {!kuraStarted ? (
                      <Button
                        variant="ghost"
                        className="justify-start"
                        onClick={() => handleStartKura(false)}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Kurayı Başlat
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        className="justify-start text-red-600 hover:text-red-700"
                        onClick={handleStopKura}
                        disabled={isSingleDrawMode || waitingForKuraStopped}
                      >
                        <Play className="h-4 w-4 mr-2 rotate-180" />
                        {isSingleDrawMode || waitingForKuraStopped
                          ? "Tek Çekiliş Devam Ediyor"
                          : "Kurayı Durdur"}
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>
      <div
        className="fixed top-0 left-0 -z-10 h-screen w-screen"
        style={{
          background:
            "linear-gradient(180.23deg, rgba(22, 26, 73, 0.41) 4.54%, #1D2052 94.97%)",
        }}
      >
        <img
          src="../../lottery-start-bg.png"
          aria-hidden="true"
          className="h-full w-full object-cover"
        />
      </div>

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 min-w-[200px]"
            style={{
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Başvuru Türü İstatistikleri */}
            <button
              onClick={handleToggleFloatingQuotaTable}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <List className="h-4 w-4" />
              Başvuru Türü İstatistikleri
            </button>

            {/* Divider */}
            <div className="my-1 border-t border-gray-200 dark:border-gray-700" />

            {/* Control Buttons - Side by Side */}
            <div className="flex gap-2 px-2 py-2">
              {/* Start/Stop Kura Button */}
              <button
                onClick={() => {
                  if (kuraStarted) {
                    // If waiting for single draw to complete, block stop
                    if (waitingForKuraStopped || isSingleDrawMode) {
                      toast.warning(
                        "Tek çekiliş modu durdurulamaz. Lütfen çekilişin tamamlanmasını bekleyin."
                      );
                      return;
                    }
                    handleStopKura();
                  } else {
                    // Block if waiting for KuraStopped after single draw
                    if (waitingForKuraStopped) {
                      toast.warning(
                        "Lütfen tek çekiliş tamamlanmasını bekleyin."
                      );
                      return;
                    }
                    handleStartKura(false); // Continuous mode
                  }
                  handleCloseContextMenu();
                }}
                disabled={
                  isDetailLoading ||
                  !signalR.isConnected ||
                  (kuraStarted &&
                    (waitingForKuraStopped || isSingleDrawMode))
                }
                className={`flex-1 p-3 rounded-lg transition-all flex items-center justify-center ${
                  kuraStarted
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-green-600 hover:bg-green-700 text-white"
                } ${
                  isDetailLoading ||
                  !signalR.isConnected ||
                  (kuraStarted && (waitingForKuraStopped || isSingleDrawMode))
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                title={kuraStarted ? "Kurayı Durdur (Ctrl+M)" : "Kurayı Başlat (Ctrl+B)"}
              >
                {kuraStarted ? (
                  <Square className="h-5 w-5" />
                ) : (
                  <Play className="h-5 w-5" />
                )}
              </button>

              {/* Single Draw Button (only for lotteryKind 1 and 2, and when isTekCekilis is enabled) */}
              {(lotteryKind === 1 || isParticipantPropertyKind) &&
                kuraStartDetail?.isTekCekilis && (
                  <button
                    onClick={() => {
                      handleSingleDraw();
                      handleCloseContextMenu();
                    }}
                    disabled={
                      isDetailLoading ||
                      !signalR.isConnected ||
                      kuraStarted ||
                      waitingForKuraStopped
                    }
                    className={`flex-1 p-3 rounded-lg transition-all flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white ${
                      isDetailLoading ||
                      !signalR.isConnected ||
                      kuraStarted ||
                      waitingForKuraStopped
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    title="Tek Çekiliş (Ctrl+Y)"
                  >
                    <Trophy className="h-5 w-5" />
                  </button>
                )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Quota Table */}
      <FloatingWindow
        visible={showFloatingQuotaTable}
        className="w-auto"
      >
        <Card className="border-[#5367B0] bg-[#171D49] shadow-2xl">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              Başvuru Türü İstatistikleri
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full hover:bg-white/10 text-white hover:text-white"
              onClick={handleCloseFloatingQuotaTable}
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Table Header */}
              <div
                className="grid grid-cols-3 rounded-xl text-[#c6c6c6]"
              >
                <div className="px-4 py-3 text-center text-sm font-semibold">
                  Başvuru Türü Adı
                </div>
                <div className="px-4 py-3 text-center text-sm font-semibold">
                  Kontenjan
                </div>
                <div className="px-4 py-3 text-center text-sm font-semibold">
                  Katılımcı
                </div>
              </div>

              {/* Table Rows */}
              <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                {quotaCategories && quotaCategories.length > 0 ? (
                  quotaCategories.map((category, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-3 px-2 items-center"
                      style={{
                        border: "2px solid #5367B0",
                        boxShadow: "0px 1px 4.5px 0px #5367B0A8",
                        borderRadius: "12px",
                      }}
                    >
                      <div className="px-2 py-3 text-center text-sm font-semibold text-white">
                        {category.label}
                      </div>
                      <div className="px-2 py-3 flex justify-center items-center">
                        <Counter
                          number={category.kontenjan}
                          setNumber={() => {}}
                          className="pointer-events-none bg-transparent border-none shadow-none"
                          slidingNumberProps={{
                            className: "text-sm font-semibold text-white",
                          }}
                          buttonProps={{
                            className: "hidden",
                          }}
                        />
                      </div>
                      <div className="px-2 py-3 flex justify-center items-center">
                        <Counter
                          number={category.toplamKatilimci}
                          setNumber={() => {}}
                          className="pointer-events-none bg-transparent border-none shadow-none"
                          slidingNumberProps={{
                            className: "text-sm font-semibold text-white",
                          }}
                          buttonProps={{
                            className: "hidden",
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-6 text-center text-sm text-gray-400">
                    Kontenjan kategorisi bulunamadı
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </FloatingWindow>
    </div>
  );
}
